// Kompetenskontroll av utsedda roller (Bas-P, Bas-U) enligt AFS 2023:3 kap. 2.
// Byggherren ska förvissa sig om att den som utses har utbildning, kompetens och erfarenhet.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BadgeCheck, Plus, Trash2 } from "lucide-react";

interface Props {
  projectId: string;
  canEdit?: boolean;
}

interface Row {
  id: string;
  role: string;
  person_name: string;
  person_email: string | null;
  education: string | null;
  experience_years: number | null;
  basis: string | null;
  notes: string | null;
  verified: boolean;
  verified_at: string | null;
}

const ROLES = [
  { value: "bas_p", label: "Bas-P" },
  { value: "bas_u", label: "Bas-U" },
  { value: "projektor", label: "Projektör" },
  { value: "entreprenor", label: "Entreprenör" },
];

const RoleCompetence: React.FC<Props> = ({ projectId, canEdit = true }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("role_competence_verifications")
      .select(
        "id, role, person_name, person_email, education, experience_years, basis, notes, verified, verified_at"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte läsa kompetenskontrollerna");
    setRows((data as Row[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("role_competence_verifications").insert({
      project_id: projectId,
      role: "bas_p",
      person_name: "",
      created_by: auth.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte lägga till raden");
    load();
  };

  const patch = async (id: string, p: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
    const { error } = await supabase
      .from("role_competence_verifications")
      .update(p)
      .eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const remove = async (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("role_competence_verifications").delete().eq("id", id);
  };

  const verify = async (row: Row) => {
    if (!row.person_name.trim() || !row.education?.trim()) {
      toast.error("Fyll i namn och utbildning innan du godkänner");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, verified: true, verified_at: new Date().toISOString() } : r
      )
    );
    const { error } = await supabase
      .from("role_competence_verifications")
      .update({
        verified: true,
        verified_at: new Date().toISOString(),
        verified_by: auth.user?.id ?? null,
      })
      .eq("id", row.id);
    if (error) return toast.error("Kunde inte spara godkännandet");
    toast.success("Kompetensen är kontrollerad och kvitterad");
  };

  if (loading) return <p className="py-6 text-center text-muted-foreground">Laddar…</p>;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <BadgeCheck className="h-4 w-4 text-primary" />
            Kompetenskontroll av utsedda roller
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            Byggherren ska kontrollera att den som utses till Bas-P och Bas-U har utbildning,
            kompetens och erfarenhet för uppdraget. AFS 2023:3 kap. 2.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={add}>
            <Plus className="mr-1 h-4 w-4" /> Lägg till
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Ingen kompetenskontroll registrerad ännu.
          </p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="grid gap-3 rounded-md border p-3 sm:grid-cols-3">
            <div>
              <Label>Roll</Label>
              <Select
                value={r.role}
                onValueChange={(v) => patch(r.id, { role: v })}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Namn</Label>
              <Input
                value={r.person_name}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { person_name: e.target.value })}
              />
            </div>
            <div>
              <Label>E-post</Label>
              <Input
                value={r.person_email ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { person_email: e.target.value })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Utbildning</Label>
              <Input
                value={r.education ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { education: e.target.value })}
                placeholder="T.ex. BAS-P/U-utbildning 2024, arbetsmiljöutbildning"
              />
            </div>
            <div>
              <Label>År i rollen</Label>
              <Input
                type="number"
                min={0}
                value={r.experience_years ?? ""}
                disabled={!canEdit}
                onChange={(e) =>
                  patch(r.id, {
                    experience_years: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </div>
            <div className="sm:col-span-3">
              <Label>Underlag och intyg</Label>
              <Textarea
                rows={2}
                value={r.basis ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { basis: e.target.value })}
                placeholder="Kursintyg, CV, referensprojekt – vad kontrollen bygger på"
              />
            </div>
            <div className="sm:col-span-3">
              <Label>Anteckning</Label>
              <Input
                value={r.notes ?? ""}
                disabled={!canEdit}
                onChange={(e) => patch(r.id, { notes: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between sm:col-span-3">
              {r.verified ? (
                <Badge variant="secondary">
                  Kontrollerad
                  {r.verified_at
                    ? ` ${new Date(r.verified_at).toLocaleDateString("sv-SE")}`
                    : ""}
                </Badge>
              ) : (
                <Badge variant="outline">Inte kontrollerad</Badge>
              )}
              {canEdit && (
                <div className="flex gap-2">
                  {!r.verified && (
                    <Button size="sm" onClick={() => verify(r)}>
                      Godkänn kompetensen
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default RoleCompetence;
